import { SimpleResult } from "./simple-result";
import {
  ILabelItem,
  ITranslatableItem,
  TError,
  TTranslationParams,
} from "./simple-result.models";

import { ERRORS } from "../constants";

export const isTranslatableItem = (
  error: TError | undefined,
): error is ITranslatableItem =>
  typeof error === "object" &&
  error !== null &&
  "translationKey" in error &&
  typeof (error as ITranslatableItem).translationKey === "string";

export const isLabelItem = (error: TError | undefined): error is ILabelItem =>
  typeof error === "object" &&
  error !== null &&
  "label" in error &&
  !("translationKey" in error);

export const createSuccessResult = <T>(
  data?: T,
  debugInfo?: unknown,
): SimpleResult<T> => new SimpleResult<T>({ success: true, data, debugInfo });

export const createSuccessResultVoid = (
  debugInfo?: unknown,
): SimpleResult<void> => new SimpleResult<void>({ success: true, debugInfo });

/**
 * The usual failure: a key from `ERRORS`, plus an optional untranslated detail
 * for logs. `label` is never shown to a user — the catalogue text is.
 */
export const createErrorResult = <T>(
  translationKey: string,
  label?: string,
  debugInfo?: unknown,
): SimpleResult<T> =>
  new SimpleResult<T>({ error: { translationKey, label }, debugInfo });

/** Same, when the message interpolates something. */
export const createErrorResultExt = <T>({
  translationKey,
  translationParams,
  label,
  data,
  debugInfo,
}: {
  translationKey: string;
  translationParams?: TTranslationParams;
  label?: string;
  data?: T;
  debugInfo?: unknown;
}): SimpleResult<T> =>
  new SimpleResult<T>({
    error: { translationKey, translationParams, label },
    data,
    debugInfo,
  });

/**
 * `translationKey` defaults to `ERRORS.common.unknown` — the vast majority of
 * callers catch a genuine crash with nothing more specific to say, and
 * shouldn't have to repeat that key at every call site. Pass one explicitly
 * only when the boundary actually knows a better key.
 */
export const createErrorResultFromException = <T>(
  error: unknown,
  translationKey: string = ERRORS.common.unknown,
  debugInfo?: unknown,
): SimpleResult<T> =>
  new SimpleResult<T>({
    error: {
      translationKey,
      label: error instanceof Error ? error.message : undefined,
    },
    debugInfo: debugInfo ?? error,
  });

/**
 * Re-types a failed result for a caller expecting different data. Only valid on
 * a failure — there is no `data` to lose. Saves an `as SimpleResult<never>`.
 */
export const castErrorResult = <T>(
  result: SimpleResult<unknown>,
): SimpleResult<T> => result as SimpleResult<T>;
